<script setup>
  import { ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import axios from '../../services/ApiService.js';
  import Modal from '../ui/Modal.vue';

  const { t } = useI18n();

  const props = defineProps({
    show: {
      type: Boolean,
      required: true
    },
    lang: {
      type: String,
      required: true
    }
  });

  const emit = defineEmits(['close', 'select']);

  const breadcrumbs = ref([]);
  const items = ref([]);
  const loading = ref(false);
  const selected = ref(null);

  const fetchChildren = async (code) => {
    loading.value = true;
    selected.value = null;
    try {
      const response = await axios.get('/api/library_catalog/category_children.json', {
        params: { code, lang: props.lang }
      });
      items.value = response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      items.value = [];
    } finally {
      loading.value = false;
    }
  };

  const reset = () => {
    breadcrumbs.value = [];
    items.value = [];
    selected.value = null;
  };

  watch(() => props.show, (newVal) => {
    if (newVal) {
      reset();
      fetchChildren(null);
    }
  });

  const drillInto = (item) => {
    breadcrumbs.value.push({ code: item.id, name: item.name });
    fetchChildren(item.id);
  };

  const navigateToRoot = () => {
    breadcrumbs.value = [];
    fetchChildren(null);
  };

  const navigateToBreadcrumb = (index) => {
    breadcrumbs.value = breadcrumbs.value.slice(0, index + 1);
    fetchChildren(breadcrumbs.value[index].code);
  };

  const handleSave = () => {
    emit('select', { id: selected.value.id, name: selected.value.name });
    emit('close');
  };

  const handleClose = () => {
    reset();
    emit('close');
  };
</script>

<template>
  <Modal :show="show" size="medium" @close="handleClose">
    <template #title>
      {{ t('components.library_catalog.category_browser.title') }}
    </template>

    <nav v-if="breadcrumbs.length > 0" aria-label="breadcrumb">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <a href="#" @click.prevent="navigateToRoot">
            {{ t('components.library_catalog.category_browser.root') }}
          </a>
        </li>
        <li
          v-for="(crumb, i) in breadcrumbs"
          :key="i"
          :class="['breadcrumb-item', { active: i === breadcrumbs.length - 1 }]"
        >
          <a v-if="i < breadcrumbs.length - 1" href="#" @click.prevent="navigateToBreadcrumb(i)">
            {{ crumb.name }}
          </a>
          <span v-else>{{ crumb.name }}</span>
        </li>
      </ol>
    </nav>

    <div v-if="loading" class="text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary" role="status">
        <span class="visually-hidden">{{ t('components.library_catalog.loading') }}</span>
      </div>
    </div>

    <ul v-else class="list-group list-group-flush category-browser-list ms-2">
      <li v-for="item in items" :key="item.id" class="list-group-item px-0">
        <button
          v-if="item.has_children"
          type="button"
          class="btn btn-link text-start w-100 p-0 text-decoration-none text-dark"
          @click="drillInto(item)"
        >
          {{ item.name }} ({{ item.child_count }}) <span class="text-muted">›</span>
        </button>

        <div v-else class="form-check">
          <input
            type="radio"
            :id="`cat-${item.id}`"
            :value="item"
            v-model="selected"
            class="form-check-input"
          />
          <label :for="`cat-${item.id}`" class="form-check-label w-100">
            {{ item.name }}
          </label>
        </div>
      </li>
    </ul>

    <template #footer>
      <button type="button" class="btn btn-secondary" @click="handleClose">
        {{ t('components.library_catalog.category_browser.cancel') }}
      </button>
      <button type="button" class="btn btn-primary" :disabled="!selected" @click="handleSave">
        {{ t('components.library_catalog.category_browser.save') }}
      </button>
    </template>
  </Modal>
</template>

<style scoped>
  .category-browser-list {
    max-height: 400px;
    overflow-y: auto;
  }

  .category-browser-list li::before {
    content: none !important;
    display: none !important;
  }
</style>
